import React, { useMemo } from "react";
import {
  getWatermarkFontStack,
  measureWatermarkText,
  watermarkFontCssStyle,
  watermarkTilePositions,
  WATERMARK_CONTENT_TYPES,
} from "../../../watermark/Popup/watermarkSettings";
import {
  resolveSignPdfFontSizePx,
  resolveSignPdfLogoDisplaySize,
} from "./signPdfSettings";
import "./SignPdfThumbMarks.css";

/**
 * Tiny Sign PDF field marks for page / source thumbs (Phase C).
 * Layout helpers stay shared; chrome is Sign-owned.
 */
const SignPdfThumbMarks = ({ mark = null, watermark = null, width = 0, height = 0 }) => {
  const settings = mark || watermark;
  const w = Math.max(0, Number(width) || 0);
  const h = Math.max(0, Number(height) || 0);
  const isLogoMode = settings?.contentType === WATERMARK_CONTENT_TYPES.LOGO;
  const logoUrl = settings?.logoUrl || null;
  const hasLogo = Boolean(logoUrl);
  const rotation = Number(settings?.rotation) || 0;
  const offsetX = Number(settings?.offsetX) || 0;
  const offsetY = Number(settings?.offsetY) || 0;
  const fontFamily = getWatermarkFontStack(
    settings?.fontId || settings?.fontFamily
  );
  const fontSizePx = resolveSignPdfFontSizePx(settings, w || 140, h || 160);

  const logoSize = useMemo(() => {
    if (!isLogoMode || !hasLogo || !w || !h) {
      return { width: 0, height: 0 };
    }
    return resolveSignPdfLogoDisplaySize(
      settings,
      w,
      h,
      settings?.logoNaturalWidth,
      settings?.logoNaturalHeight,
      settings?.logoCropRect
    );
  }, [isLogoMode, hasLogo, settings, w, h]);

  const textMetrics = useMemo(
    () =>
      measureWatermarkText(
        settings?.text?.trim() ? settings.text : "Your Text",
        fontSizePx,
        fontFamily,
        settings?.fontWeight,
        {
          letterSpacing: settings?.letterSpacing,
          textScript: settings?.textScript,
        }
      ),
    [
      settings?.text,
      fontSizePx,
      fontFamily,
      settings?.fontWeight,
      settings?.letterSpacing,
      settings?.textScript,
    ]
  );

  const markMetrics = isLogoMode
    ? { width: logoSize.width, height: logoSize.height }
    : textMetrics;

  const hasContent = isLogoMode ? hasLogo : Boolean(settings?.text?.trim());

  const marks = useMemo(() => {
    if (!hasContent || !w || !h) return [];
    return watermarkTilePositions({
      tile: settings?.tile || "single",
      spacing: settings?.spacing,
      stageWidth: w,
      stageHeight: h,
      textWidth: markMetrics.width,
      textHeight: markMetrics.height,
      fontSizePx: isLogoMode
        ? Math.max(markMetrics.width, markMetrics.height)
        : fontSizePx,
      rotationDeg: rotation,
      position: settings?.position || "center",
      margin: settings?.margin,
      offsetX,
      offsetY,
      flushEdges: settings?.flushEdges !== false,
    });
  }, [
    hasContent,
    w,
    h,
    settings,
    markMetrics,
    fontSizePx,
    rotation,
    isLogoMode,
    offsetX,
    offsetY,
  ]);

  if (!settings || !hasContent || !marks.length || !w || !h) return null;

  const opacity =
    Math.max(0, Math.min(100, Number(settings?.opacity) || 0)) / 100;
  const displayText = settings?.text?.trim() ? settings.text : "Your Text";

  return (
    <div className="sp-thumb-marks" aria-hidden>
      {marks.map((pos, i) => {
        const shared = {
          left: `${pos.x * 100}%`,
          top: `${pos.y * 100}%`,
          opacity,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        };
        if (isLogoMode) {
          return (
            <img
              key={`stm-${i}`}
              src={logoUrl}
              alt=""
              className="sp-thumb-marks__logo"
              style={{
                ...shared,
                width: markMetrics.width,
                height: markMetrics.height,
              }}
              draggable={false}
            />
          );
        }
        return (
          <span
            key={`stm-${i}`}
            className="sp-thumb-marks__text"
            style={{
              ...shared,
              color: settings?.color || "#111111",
              fontFamily,
              fontSize: fontSizePx,
              fontWeight: settings?.fontWeight === "bold" ? 700 : 400,
              fontStyle: watermarkFontCssStyle(settings?.fontStyle),
            }}
          >
            {displayText}
          </span>
        );
      })}
    </div>
  );
};

export default SignPdfThumbMarks;
